import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import styles from './scrollShadowStyles.scss';

const calculateShadowIntensity = (edgeDistance: number, maxIntensity: number): number => {
  // The shadow intensity begans to fade when the viewable area while scrolling
  // gets within 50 pixels of either the start or end of the container. When either start or
  // end of the container is reached, the shadow intensity becomes 0 (no more shadow).
  // Mustafa M. 2/28/2024
  let intensity = (edgeDistance / 50) * maxIntensity;
  intensity = Math.min(intensity, maxIntensity);

  return intensity;
};

const ScrollShadow: React.FC<{
  columnReverse?: boolean;
  hasDarkBackground?: boolean;
  maxIntensity?: number;
} & HTMLAttributes<HTMLDivElement>> = ({
  columnReverse,
  hasDarkBackground,
  // maxIntensity is used to determine how dark the scroll shadow gets -
  // 0.0 through 1.0, with 1.0 being the darkest. Mustafa M. 2/28/2024
  maxIntensity = 1,
  ...contentProps
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [shadowTopIntensity, setShadowTopIntensity] = useState(0);
  const [shadowBottomIntensity, setShadowBottomIntensity] = useState(0);

  const checkShadows = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const maxScrollTop = scrollHeight - clientHeight;

      // Using Math.abs below in order to calculate the shadow intensity when the overflowing
      // content is using "flex-direction: column-reverse;" because in those instances, scrollTop is negative.
      // topIntensity also belongs to the bottom shadow and vice versa when using "flex-direction: column-reverse;"
      // Mustafa M. 2/28/2024
      const topIntensity = calculateShadowIntensity(Math.abs(scrollTop), maxIntensity);
      const bottomIntensity = calculateShadowIntensity(
        maxScrollTop - Math.abs(scrollTop),
        maxIntensity
      );

      if (columnReverse) {
        setShadowTopIntensity(bottomIntensity);
        setShadowBottomIntensity(topIntensity);
      } else {
        setShadowTopIntensity(topIntensity);
        setShadowBottomIntensity(bottomIntensity);
      }
    }
  }, [columnReverse, maxIntensity]);

  useEffect(() => {
    const container = containerRef.current;
    const mutationObserver = new MutationObserver(checkShadows);

    // Using a resize event listener on the window in order for scroll
    // shadows to adjust accordingly when the user resizes their browser.
    // For example if a user makes the browser smaller, items inside the content div
    // may begin to overflow - creating a need scroll shadows to appear. Mustafa M. 2/28/2024
    window.addEventListener('resize', checkShadows);

    if (container) {
      // Using a mutation observer in addition to scroll event listener
      // so scroll shadows adjust accordingly when new items get rendered
      // or when a <Tab /> is changed. Same reason as above - new items added or removed
      // in the content div may begin to overflow or stop overflowing
      // creating a need for scroll shadows to appear or disappear. Mustafa M. 2/28/2024
      container.addEventListener('scroll', checkShadows);
      mutationObserver.observe(container, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true
      });
    }

    return (): void => {
      window.removeEventListener('resize', checkShadows);

      if (container) {
        container.removeEventListener('scroll', checkShadows);
        mutationObserver.disconnect();
      }
    };
  }, [checkShadows]);

  return (
    <div
      className={classNames(styles.scrollShadow, { [styles.hasDarkBackground]: hasDarkBackground })}
    >
      <div
        className={styles.scrollShadowTop}
        style={{
          ['--shadow-top-intensity']: shadowTopIntensity
        }}
      />
      <div
        {...contentProps}
        className={classNames(contentProps.className, styles.scrollShadowContent)}
        ref={containerRef}
      >
        {contentProps.children}
      </div>
      <div
        className={styles.scrollShadowBottom}
        style={{ ['--shadow-bottom-intensity']: shadowBottomIntensity }}
      />
    </div>
  );
};

export default ScrollShadow;
